import Component from '../abstracts/Component';
import { isMobile, MobileBreakpoint } from '../utils/MobileUtils';

export default class ListingHeadComponent extends Component {

  get SELECTORS() {
    return {
      listingHead: '[data-listing-head]',
      fixedEl: '[data-listing-head-fixed]',
      header: '[data-js-component="HeaderComponent"]',
      resultsCount: '[data-results-count]',
      sortBy: '[data-sort]',
      sortByLayerMobile: '[data-sort-layer-mobile]',
      sortByLayerDesktop: '[data-sort-layer-desktop]',
      sortByItem: '[data-sort-item]',
      listingLayoutNormal: '[data-grid-normal]',
      listingLayoutFocus: '[data-grid-focus]',
      footerDiv: '[data-footer]'
    };
  }

  get CLASSES() {
    return {
      active: 'active',
      menuOpen: 'menu-open',
      fixed: '--fixed',
      hidden: '--hidden',
      open: 'open'
    };
  }

  constructor(compEl) {
    super(compEl);
    this.height = this.$component.getBoundingClientRect().height;
    this.header = document.querySelector(this.SELECTORS.header);
    this.footerDiv = document.querySelector(this.SELECTORS.footerDiv);
    this.listingHead = this.$component;
    this.fixedEl = this.$component.querySelector(this.SELECTORS.fixedEl);
    this.resultsCount = this.$component.querySelector(this.SELECTORS.resultsCount);
    this.sortBy = this.$component.querySelectorAll(this.SELECTORS.sortBy);
    this.sortByLayerMobile = this.$component.querySelector(this.SELECTORS.sortByLayerMobile);
    this.sortByLayerDesktop = this.$component.querySelector(this.SELECTORS.sortByLayerDesktop);
    this.sortByItem = this.$component.querySelectorAll(this.SELECTORS.sortByItem);
    this.listingLayoutNormal = this.$component.querySelector(this.SELECTORS.listingLayoutNormal);
    this.listingLayoutFocus = this.$component.querySelector(this.SELECTORS.listingLayoutFocus);
  }

  /**
   * Handles the window scroll event to adjust the position of the fixed element.
   * - Adds a fixed class to the element and sets its top position to the header's height
   *   when the listing head is scrolled past the header.
   * - Removes the fixed class and resets the top position when the listing head is above the header.
   */
  onWindowScroll() {
    let root = document.documentElement;
    const headerHeight = this.header.getBoundingClientRect().height;
    root.style.setProperty('--header--height', `${headerHeight}px`);
    this.fixedEl.style.bottom = `auto`;
    if (this.$component.getBoundingClientRect().top < this.header.getBoundingClientRect().height) {
      this.fixedEl.classList.add(this.CLASSES.fixed);
      this.fixedEl.style.top = `${headerHeight - 1}px`;
      document.querySelector('[data-listing]').style.paddingTop = `${headerHeight - 1}px`;
    } else {
      this.fixedEl.classList.remove(this.CLASSES.fixed);
      this.fixedEl.style.top = `0`;
      document.querySelector('[data-listing]').style.paddingTop = 0;
    }
    //
    if (this.footerDiv) {
      let footerPosition = this.footerDiv.offsetTop ;
      let scrolled = document.scrollingElement.scrollTop;
      let offset = this.fixedEl.clientHeight + this.header.clientHeight + 5;
      if (scrolled > (footerPosition - offset ) ) {
        this.fixedEl.style.top = `auto`;
        this.fixedEl.style.bottom = `${window.innerHeight - (footerPosition - scrolled) }px`;
      }
    }
  }

  /**
   * Sets the sort type based on the given code and emits the SORT_BY_EVENTS.changed event.
   * @param {string} code - The code of the sort type.
   */
  setSortType(code) {
    Array.from(document.querySelectorAll('[data-sort]')).every(el => el.setAttribute('data-sort', code));
    this.$emit(this.$customEvents.SORT_BY_EVENTS.changed);
  }

  /**
   * Binds event listeners to various elements within the listing head component.
   *
   * - Toggles the 'open' class for sort buttons and their corresponding mobile and desktop layers.
   * - Updates the sort type when a sort item is clicked and manages its visibility.
   * - Handles custom events to close sort layers.
   * - Switches listing layout between normal and focus modes, updating URL query parameters accordingly.
   * - Adds scroll and resize event listeners to adjust the position of the fixed element.
   */
  bindEvents() {
    this.sortBy && this.sortBy.forEach(btn => {
      btn.addEventListener('click', () => {
        if (Array.from(btn.classList).includes(this.CLASSES.open)) btn.classList.remove(this.CLASSES.open);
        else btn.classList.add(this.CLASSES.open);
        if (isMobile(MobileBreakpoint.Max767)) {
          if (this.sortByLayerMobile && this.sortByLayerMobile.classList.contains(this.CLASSES.open)) {
            this.sortByLayerMobile.classList.remove(this.CLASSES.open);
          } else {
            this.sortByLayerMobile && this.sortByLayerMobile.classList.add(this.CLASSES.open);
          }
        } else {
          if (this.sortByLayerDesktop && this.sortByLayerDesktop.classList.contains(this.CLASSES.open)) {
            this.sortByLayerDesktop.classList.remove(this.CLASSES.open);
          } else {
            this.sortByLayerDesktop && this.sortByLayerDesktop.classList.add(this.CLASSES.open);
            this.$emit(this.$customEvents.FILTERS_EVENTS.closeItems);
          }
        }
      });
    });

    this.sortByItem.length && this.sortByItem.forEach(item => {
      item.addEventListener('click', () => {
        if (item.checked) {
          const otherItems = Array.from(this.sortByItem).filter(i => i.id !== item.id);
          const thisItems = Array.from(this.sortByItem).filter(i => i.id == item.id);
          otherItems.forEach(i => i.parentElement.parentElement.querySelector('i').classList.add(this.CLASSES.hidden));
          thisItems.forEach(i => i.parentElement.parentElement.querySelector('i').classList.remove(this.CLASSES.hidden));
          this.setSortType(item.id);
        } else {
          item.parentElement.parentElement.querySelector('i').classList.add('--hidden');
        }
      });
    });

    this.$on(this.$customEvents.SORT_BY_EVENTS.close, () => {
      this.sortBy.forEach(item => item.classList.remove(this.CLASSES.open))
      this.sortByLayerMobile && this.sortByLayerMobile.classList.remove(this.CLASSES.open);
      this.sortByLayerDesktop && this.sortByLayerDesktop.classList.remove(this.CLASSES.open);
    });

    this.listingLayoutNormal && this.listingLayoutNormal.addEventListener('click', () => {
      const gridNormal = this.listingLayoutNormal.dataset.gridNormal;
      if (gridNormal.toLowerCase() === 'false') {
        this.listingLayoutNormal.setAttribute('data-grid-normal', true);
        this.listingLayoutFocus?.setAttribute('data-grid-focus', false);
        this.listingLayoutNormal.classList.add(this.CLASSES.active);
        this.listingLayoutFocus?.classList.remove(this.CLASSES.active);
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set('focus', 'false');
        this.$emit(this.$customEvents.LISTING_LAYOUT_EVENTS.changed, queryParams);
      }
    });

    this.listingLayoutFocus && this.listingLayoutFocus.addEventListener('click', () => {
      const gridFocus = this.listingLayoutFocus.dataset.gridFocus;
      if (gridFocus.toLowerCase() === 'false') {
        this.listingLayoutFocus.setAttribute('data-grid-focus', true);
        this.listingLayoutNormal?.setAttribute('data-grid-normal', false);
        this.listingLayoutNormal?.classList.remove(this.CLASSES.active);
        this.listingLayoutFocus.classList.add(this.CLASSES.active);
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set('focus', 'true');
        this.$emit(this.$customEvents.LISTING_LAYOUT_EVENTS.changed, queryParams);
      }
    });

    this.$on(this.$customEvents.FILTERS_EVENTS.updated, ({payload}) => {
      console.log({payload})
      this.resultsCount.innerHTML = payload.resultsCount.innerHTML;
    });

    window.addEventListener('scroll', () => this.onWindowScroll());
    window.addEventListener('resize', () => this.onWindowScroll());
  }

  render() {
    super.render();
    this.bindEvents();
  }
}
